"use client";

import { useCallback, useState } from "react";

// Components
import HeaderTabs from "./header/HeaderTabs";
import PositionsList from "./content/positions/PositionsList";
import OrdersList from "./content/orders/OrdersList";
import DealsList from "./content/deals/DealsList";
import Summary from "./footer/Summary";

// Types
import type { TradeOrder } from "@/vybe-trader-client/trade-history/types";
import type {
  VybeOrderInternal,
  VybeDealInternal,
} from "@/vybe-trader-client/trade-history/types";

// VybeTrader Store
import { useTradeHistoryStore } from "@/vybe-trader-client/trade-history/stores/useTradeHistoryStore";
import PositionDetailsDrawer from "./content/positions/PositionDetailsDrawer";
import OrderDetailsDrawer from "./content/orders/OrderDetailsDrawer";
import DealDetailsDrawer from "./content/deals/DealDetailsDrawer";
import HistoryFilterScreen from "./header/filter/HistoryFilterScreen";
import { useFilterStore } from "@/vybe-trader-client/trade-history/stores/useFilterStore";
import {
  useFilterPositions,
  useFilterOrders,
  useFilterDeals,
} from "@/vybe-trader-client/trade-history/hooks/useFilterTrades";
import {
  exportPositionsCsv,
  exportOrdersCsv,
  exportDealsCsv,
} from "./historyCsvExport";

export type TabType = "positions" | "orders" | "deals";

interface HistoryScreenProps {
  onNavigateToChart?: (symbol: string) => void;
  onNavigateToTrade?: (symbol: string) => void;
}

export default function HistoryScreen({
  onNavigateToChart,
  onNavigateToTrade,
}: HistoryScreenProps) {
  const [activeTab, setActiveTab] = useState<TabType>("positions");
  const [showFilter, setShowFilter] = useState(false);
  const [selectedPositionDetails, setSelectedPositionDetails] =
    useState<TradeOrder | null>(null);
  const [selectedOrderDetails, setSelectedOrderDetails] =
    useState<VybeOrderInternal | null>(null);
  const [selectedDealDetails, setSelectedDealDetails] =
    useState<VybeDealInternal | null>(null);

  const selectedSymbol = useFilterStore((state) => state.selectedSymbol);
  const dateFilter = useFilterStore((state) => state.dateFilter);
  const customDateFrom = useFilterStore((state) => state.customDateFrom);
  const customDateTo = useFilterStore((state) => state.customDateTo);

  const filterCfg = {
    symbol: selectedSymbol,
    dateFilter,
    customDateFrom,
    customDateTo,
  };

  const filteredPositions = useFilterPositions(filterCfg);
  const filteredOrders = useFilterOrders(filterCfg);
  const filteredDeals = useFilterDeals(filterCfg);

  const handleExportCsv = useCallback(() => {
    switch (activeTab) {
      case "positions":
        exportPositionsCsv(filteredPositions);
        break;
      case "orders":
        exportOrdersCsv(filteredOrders);
        break;
      case "deals":
        exportDealsCsv(filteredDeals);
        break;
      default:
        break;
    }
  }, [activeTab, filteredPositions, filteredOrders, filteredDeals]);

  const isLoading = useTradeHistoryStore((state) => state.isLoading());

  const handlePositionClick = (position: TradeOrder) => {
    setSelectedPositionDetails(position);
  };

  const handleOrderClick = (order: VybeOrderInternal) => {
    setSelectedOrderDetails(order);
  };

  const handleDealClick = (deal: VybeDealInternal) => {
    setSelectedDealDetails(deal);
  };

  const handleChart = (symbol: string) => {
    if (onNavigateToChart) {
      onNavigateToChart(symbol);
    }
  };

  const handleTrade = (symbol: string) => {
    if (onNavigateToTrade) {
      onNavigateToTrade(symbol);
    }
  };

  if (showFilter) {
    return <HistoryFilterScreen onBack={() => setShowFilter(false)} />;
  }

  return (
    <div className="h-full bg-background text-foreground flex flex-col">
      <HeaderTabs
        activeTab={activeTab}
        onTabChange={setActiveTab}
        onClockClick={() => setShowFilter(true)}
        onExportCsv={handleExportCsv}
      />

      <div className="flex-1 overflow-hidden px-3">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <p className="text-muted-foreground">Loading trades...</p>
          </div>
        ) : (
          <>
            {activeTab === "positions" && (
              <PositionsList onClick={handlePositionClick} />
            )}
            {activeTab === "orders" && (
              <OrdersList onClick={handleOrderClick} />
            )}
            {activeTab === "deals" && <DealsList onClick={handleDealClick} />}
          </>
        )}
      </div>

      <Summary activeTab={activeTab} />

      <PositionDetailsDrawer
        isOpen={!!selectedPositionDetails && activeTab === "positions"}
        onClose={() => setSelectedPositionDetails(null)}
        position={selectedPositionDetails}
        onChart={() =>
          selectedPositionDetails &&
          handleChart(selectedPositionDetails.symbol)
        }
        onTrade={() =>
          selectedPositionDetails &&
          handleTrade(selectedPositionDetails.symbol)
        }
      />

      <OrderDetailsDrawer
        isOpen={!!selectedOrderDetails && activeTab === "orders"}
        onClose={() => setSelectedOrderDetails(null)}
        order={selectedOrderDetails}
        onChart={() =>
          selectedOrderDetails && handleChart(selectedOrderDetails.symbol)
        }
        onTrade={() =>
          selectedOrderDetails && handleTrade(selectedOrderDetails.symbol)
        }
      />

      <DealDetailsDrawer
        isOpen={!!selectedDealDetails && activeTab === "deals"}
        onClose={() => setSelectedDealDetails(null)}
        deal={selectedDealDetails}
        onChart={() =>
          selectedDealDetails && handleChart(selectedDealDetails.symbol)
        }
        onTrade={() =>
          selectedDealDetails && handleTrade(selectedDealDetails.symbol)
        }
      />
    </div>
  );
}
